import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import {
    DocumentDto,
    DocumentService,
    ShareDocumentDto,
    TicketService,
    UserService,
} from "@malirang/api-client";
import {
    Button,
    Icon,
    ModalAdapter,
    ModalContainer,
    ModalHeader,
} from "@malirang/front-share";
import { injectQueryParams } from "ngxtension/inject-query-params";
import {
    DocumentShareParentItemComponent,
    DocumentShareParentItemOutput,
} from "./document-share-parent-item/document-share-parent-item.component";
import { lastValueFrom, merge, of, share } from "rxjs";

export type DocumentShareParentModalProps = {
    documents: DocumentDto[];
};

@Component({
    selector: "app-document-share-parent",
    templateUrl: "./document-share-parent.modal.html",
    styleUrls: ["./document-share-parent.modal.css"],
    imports: [
        CommonModule,
        ModalHeader,
        ModalContainer,
        Button,
        Icon,
        DocumentShareParentItemComponent,
    ],
})
export class DocumentShareParentModal extends ModalAdapter<DocumentShareParentModalProps> {
    readonly documentService = inject(DocumentService);
    readonly userService = inject(UserService);
    readonly ticketService = inject(TicketService);

    items = signal<ShareDocumentDto[]>([]);
    cancelledItems = signal<ShareDocumentDto[]>([]);

    ticketId = injectQueryParams("ticketId");
    $ticket = rxResource({
        stream: () =>
            this.ticketService.ticketControllerFindById({
                id: this.ticketId() || "",
            }),
    });
    childId = computed(
        () => this.$ticket.value()?.enrollment?.centerChild?.child?.id,
    );

    $parents = rxResource({
        stream: ({ params }) =>
            params.childId
                ? this.userService.parentControllerFindParent({
                      childId: params.childId,
                  })
                : of([]),
        params: () => ({
            childId: this.childId(),
        }),
    });
    parents = this.$parents.value;

    documents = computed(() => this.props()?.documents || []);

    _firstCounseling = computed(() => {
        return this.documents().find(
            (doc) =>
                doc.preServiceType === "기본상담지" ||
                doc.preServiceType === "심화상담지",
        );
    });

    $firstCounseling = rxResource({
        stream: ({ params }) =>
            params.id
                ? this.documentService.documentControllerFindById({
                      id: params.id || "",
                  })
                : of(null),
        params: () => ({
            id: this._firstCounseling()?.id || "",
        }),
    });
    firstCounseling = computed(() =>
        this.$firstCounseling.hasValue() ? this.$firstCounseling.value() : null,
    );

    $report = rxResource({
        stream: ({ params }) =>
            params.id
                ? this.documentService.documentControllerFindById({
                      id: params.id || "",
                  })
                : of(null),
        params: () => ({
            id: this._report()?.id || "",
        }),
    });
    report = computed(() =>
        this.$report.hasValue() ? this.$report.value() : null,
    );
    _report = computed(() => {
        return this.documents().find(
            (doc) => doc.inServiceType === "중간보고서",
        );
    });

    $endReport = rxResource({
        stream: ({ params }) =>
            params.id
                ? this.documentService.documentControllerFindById({
                      id: params.id || "",
                  })
                : of(null),
        params: () => ({
            id: this._endReport()?.id || "",
        }),
    });
    endReport = computed(() =>
        this.$endReport.hasValue() ? this.$endReport.value() : null,
    );

    _endReport = computed(() => {
        return this.documents().find(
            (doc) => doc.inServiceType === "종결보고서",
        );
    });

    refresh() {
        this.$parents.reload();
        this.$firstCounseling.reload();
        this.$report.reload();
        this.$endReport.reload();
    }

    allCancel() {
        const body: ShareDocumentDto[] = [];

        const documentIds = this.documents().map((doc) => doc.id!);
        for (const parent of this.parents() || []) {
            body.push({
                documentIds,
                parentId: parent.id!,
            });
        }

        this.documentService
            .documentControllerCancelShareDocument({
                body,
            })
            .subscribe({
                next: () => {
                    this.refresh();
                    this.toastService.success(
                        "문서 공유가 전체 취소되었습니다",
                    );
                },
            });
    }

    handleOutput(ev: DocumentShareParentItemOutput) {
        const { cancelledDocumentIds, documentIds, parentId } = ev;

        if (this.items()?.find((i) => i.parentId === parentId)) {
            this.items.set([
                ...this.items().filter((i) => i.parentId !== parentId),
                { parentId, documentIds },
            ]);
        } else {
            this.items.set([
                ...this.items().filter((i) => i.parentId !== parentId),
                { parentId, documentIds },
            ]);
        }

        if (this.cancelledItems()?.find((i) => i.parentId === parentId)) {
            this.cancelledItems.set([
                ...this.cancelledItems().filter((i) => i.parentId !== parentId),
                { parentId, documentIds: cancelledDocumentIds },
            ]);
        } else {
            this.cancelledItems.set([
                ...this.cancelledItems().filter((i) => i.parentId !== parentId),
                { parentId, documentIds: cancelledDocumentIds },
            ]);
        }
    }

    async submit() {
        const cancel$ = this.cancelledItems().length
            ? this.documentService.documentControllerCancelShareDocument({
                  body: this.cancelledItems(),
              })
            : of(null);

        const share$ = this.items().length
            ? this.documentService.documentControllerShareDocument({
                  body: this.items(),
              })
            : of(null);

        const res1 = await lastValueFrom(cancel$);
        const res2 = await lastValueFrom(share$);

        if (res1 && res2) {
            this.toastService.success("문서 공유 설정이 저장되었습니다");
            this.close("open");
        }
    }
}
